<template>
  <div>
    <c-button class="c-header-nav-btn" @click="showSearchModal = true">Search</c-button>
    <search-modal title="Search" :show.sync="showSearchModal" :close-on-backdrop="true" dialog-classes="p-0">
      <c-input
        id="searchField"
        v-model="searchTerm"
        type="search"
        placeholder="KoalaBoss Search"
        autofocus
        size="lg"
        class="m-3"
        autocomplete="off"
        @keyup="doSearch"
      />

      <div v-if="searchTerm.length >= 3 && !clients.length && !sites.length && !plugins.length && !themes.length">
        <h2 class="h4 text-center mt-2">No results</h2>
        <div class="text-center my-4">
          <c-button v-if="urlEntered" color="primary" @click="addSite(urlParsed)">
            <c-icon :content="freeSet.cilPlus" />
            Add <strong>{{urlParsed}}</strong> as new site
          </c-button>
        </div>
      </div>

      <div v-else class="search-results" style="max-height: 300px; overflow: auto;">
        <c-row>
          <c-col :lg="selectedSite ? 6 : 12">
            <div v-if="sites.length">
              <div class="search-header">Sites</div>
              <ul>
                <li v-for="site in sites" :key="site._id" class="mt-2">
                  <a href="#" @click="openSite(site)" v-html="site.name || site.siteAddress" />
                  <br />
                  <small class="text-muted">{{ site.siteAddress }}</small>
                </li>
              </ul>
            </div>
            <div v-if="clients.length" class="mt-3">
              <div class="search-header">Clients</div>
              <ul>
                <li v-for="client in clients" :key="client._id">
                  <a href="#" @click="openClient(client)" v-html="client.name" />
                </li>
              </ul>
            </div>
            <div v-if="plugins.length" class="mt-3">
              <div class="search-header">Plugins</div>
              <ul>
                <li v-for="plugin in plugins" :key="plugin._id">
                  <c-row class="mb-1" @click="openPlugin(plugin)">
                    <c-col sm="2">
                      <img v-lazy="getPluginThumbUrl(plugin.pluginKey)" style="height: 20px" />
                    </c-col>
                    <c-col sm="10">
                      <a href="#" @click="openPlugin(plugin)" v-html="plugin.name" />
                    </c-col>
                  </c-row>
                </li>
              </ul>
            </div>
            <div v-if="themes.length" class="mt-3">
              <div class="search-header">Themes</div>
              <ul>
                <li v-for="theme in themes" :key="theme._id">
                  <c-row class="mb-1" @click="openTheme(theme)">
                    <c-col sm="2">
                      <img v-lazy="getThemeThumbUrl(theme.key)" style="height: 20px" />
                    </c-col>
                    <c-col sm="10">
                      <a href="#" @click="openTheme(theme)" v-html="theme.name + ` (${theme.key})`" />
                    </c-col>
                  </c-row>
                </li>
              </ul>
            </div>
          </c-col>
          <c-col v-show="selectedSite" lg="6">
            <div v-if="selectedSite" class="px-3">
              <p v-html="selectedSite.name" />
              <c-img :src="selectedSiteThumbnail" class="w-100" />
            </div>
          </c-col>
        </c-row>
      </div>

      <p>{{ error }}</p>
    </search-modal>
  </div>
</template>

<script>
import Vue from 'vue';
import _ from 'lodash';
import VueLazyload from 'vue-lazyload';
import { freeSet } from '@coreui/icons';
import SearchModal from './SearchModal';
import { buildEndpoint } from '@/libs/buildEndPoint';
import { getPluginThumbUrl } from '@/libs/plugin/getPluginThumbUrl';
import { getThemeThumbUrl } from '@/libs/theme/getThemeThumbUrl';

const placeholderImage = process.env.VUE_APP_PLUGIN_PLACEHOLDER_IMAGE_URL;

Vue.use(VueLazyload, {
  preLoad: 1.3,
  error: placeholderImage,
  loading: placeholderImage,
  attempt: 1,
});

export default {
  name: 'SearchAll',
  components: {
    SearchModal,
  },
  data() {
    return {
      showSearchModal: false,
      searchTerm: '',
      error: null,
      sites: [],
      clients: [],
      plugins: [],
      themes: [],
      selectedSite: null,
      selectedSiteThumbnail: null,
      selectedClient: null,
      freeSet,
    };
  },
  computed: {
    urlEntered() {
      return this.isUrl(this.searchTerm);
    },
    urlParsed() {
      try {
        if (this.isUrl(this.searchTerm)) {
          return new URL(!this.searchTerm.startsWith('http') ? `https://${this.searchTerm}` : this.searchTerm).host;
        }
        return false;
      } catch (e) {
        return false;
      }
    },
  },
  watch: {
    showSearchModal(val) {
      if (val) {
        this.reset();
      }
    },
  },
  mounted() {
    document.getElementById('searchField').focus();
  },
  updated() {
    document.getElementById('searchField').focus();
  },
  methods: {
    reset() {
      this.searchTerm = '';
      this.selectedSite = null;
      this.selectedSiteThumbnail = null;
      this.selectedClient = null;
      this.sites = [];
      this.clients = [];
      this.plugins = [];
      this.themes = [];
    },
    getPluginThumbUrl,
    getThemeThumbUrl,
    async addSite(url) {
      this.showSearchModal = false;
      if (this.$route.name === 'addASite') {
        await this.$router.push('/dashboard');
      }
      this.$router.push(`/add-a-site?url=${url}`);
    },
    openSite(site) {
      this.showSearchModal = false;
      setTimeout(() => {
        // this is a hack to get around the page not loading, if you are already on a site details page.
        // @TODO - find a better way
        this.$router.push('/dashboard').finally(() => {
          // eslint-disable-next-line no-underscore-dangle
          this.$router.push({ name: 'detailsSite', params: { siteId: site._id } }).catch((err) => {
            console.log(err);
          });
        });
      }, 250);
    },
    openClient(client) {
      this.showSearchModal = false;
      setTimeout(() => {
        // this is a hack to get around the page not loading, if you are already on a site details page.
        // @TODO - find a better way
        this.$router.push('/dashboard').finally(() => {
          // eslint-disable-next-line no-underscore-dangle
          this.$router.push({ name: 'clientDetails', params: { clientId: client._id } }).catch((err) => {
            console.log(err);
          });
        });
      }, 250);
    },
    openPlugin(plugin) {
      this.showSearchModal = false;
      setTimeout(() => {
        // this is a hack to get around the page not loading, if you are already on a site details page.
        // @TODO - find a better way
        this.$router.push('/dashboard').finally(() => {
          this.$router.push({ name: 'pluginDetails', params: { pluginKey: plugin.pluginKey } }).catch((err) => {
            console.log(err);
          });
        });
      }, 250);
    },
    openTheme(theme) {
      this.showSearchModal = false;
      setTimeout(() => {
        // this is a hack to get around the page not loading, if you are already on a site details page.
        // @TODO - find a better way
        this.$router.push('/dashboard').finally(() => {
          this.$router.push({ name: 'themeDetails', params: { themeKey: theme.key } }).catch((err) => {
            console.log(err);
          });
        });
      }, 250);
    },
    getSiteThumb() {
      this.selectedSiteThumbnail = _.get(this.selectedSite, 'screenshots.desktop.thumb.url');
    },
    isUrl(str) {
      const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?(\?.*)?(#.*)?$/;
      return urlRegex.test(str);
    },
    doSearch() {
      // eslint-disable-next-line no-underscore-dangle
      const _this = this;
      if (this.searchTerm.length >= 3) {
        // Filter out url parts, so we can search on urls correctly

        let searchTerm = this.searchTerm.trim();

        if (this.isUrl(searchTerm)) {
          if (!searchTerm.startsWith('http')) {
            searchTerm = `https://${searchTerm}`;
          }
          try {
            const parsedUrl = new URL(searchTerm);
            searchTerm = parsedUrl.host.replace('www.', '');
          } catch (e) {
            // Invalid url
          }
        }

        this.$http
          .get(buildEndpoint('search/all?term=') + searchTerm)
          .then((response) => {
            this.error = null;
            if (response.data.success) {
              // console.log(response.data)
              // console.log(_.get(response, 'data.sites'));
              _this.sites = _.get(response, 'data.data.sites');
              _this.selectedSite = _.first(_this.sites);
              _this.getSiteThumb();
              _this.clients = _.get(response, 'data.data.clients');
              _this.plugins = _.get(response, 'data.data.plugins');
              _this.themes = _.get(response, 'data.data.themes');
            } else {
              _this.error = 'Error doing search';
            }
          })
          .catch((error) => {
            _this.error = error;
          });
      }
    },
  },
};
</script>

<style lang="scss">
.search-header {
  width: 100%;
  padding: 3px;
  background: #5fceb3;
  font-size: 12px;
  text-transform: uppercase;
}

.c-dark-theme {
  .search-header {
    width: 100%;
    padding: 3px;
    background: #424249;
    font-size: 12px;
    text-transform: uppercase;
  }
}

.search-results {
  ul {
    list-style-type: none !important;
    margin: 10px 0;
    padding: 0;

    li {
      margin: 0;
      padding-left: 10px;
    }
  }
}
</style>
